import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  IconButton,
  Divider,
  useMediaQuery,
  Tooltip,
  Avatar,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Logout,
  Dashboard as DashboardIcon,
  Assignment,
  Description,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  ReceiptLong,
  QueryStats,
  Assessment,
  Api,
  ManageAccounts
} from '@mui/icons-material';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import InspectionPage from './components/InspectionPage';
import ReassignmentPage from './components/ReassignmentPage';
import LanguageSwitcher from './components/LanguageSwitcher';
import CreateCasePage from './components/CreateCasePage';
import ServiceLogsPage from './components/ServiceLogsPage';
import GraphsPage from './components/GraphsPage';
import ReportsPage from './components/ReportsPage';
import ApiDocumentationPage from './components/ApiDocumentationPage';
import ResetPassword from './components/ResetPassword';
import UserManagementPage from './components/UserManagementPage';
import { authService } from './services/api';
import { createAppTheme } from './theme';
import logo from './assets/logo2.png';
import './App.css';
import { usePortalText } from './locales/portalTranslations';

function App() {
  const { t, i18n } = useTranslation();
  const pt = usePortalText();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [activePage, setActivePage] = useState('dashboard');
  const [loginNotice, setLoginNotice] = useState('');
  const isMobile = useMediaQuery('(max-width:900px)');

  // Determine if current language is RTL
  const isRTL = i18n.language === 'ar';

  // Create a theme dynamically based on language direction
  const theme = createAppTheme(isRTL ? 'rtl' : 'ltr');

  useEffect(() => {
    let active = true;
    authService.getCurrentUser()
      .then(response => {
        if (!active) return;
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      })
      .catch(() => { if (active) localStorage.removeItem('user'); })
      .finally(() => { if (active) setLoading(false); });

    // Close drawer by default on mobile
    if (isMobile) {
      setDrawerOpen(false);
    }
    const sessionExpired = event => {
      localStorage.removeItem('user');
      localStorage.removeItem('username');
      setLoginNotice(event.detail || 'Your session expired. Please sign in again.');
      setUser(null);
    };
    window.addEventListener('auth-session-expired', sessionExpired);
    return () => {
      active = false;
      window.removeEventListener('auth-session-expired', sessionExpired);
    };
  }, [isMobile]);

  useEffect(() => {
    if (!user) return undefined;
    let idleTimer;
    const resetIdleTimer = () => {
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(handleLogout, 30 * 60 * 1000);
    };
    const events = ['click', 'keydown', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      window.clearTimeout(idleTimer);
      events.forEach(event => window.removeEventListener(event, resetIdleTimer));
    };
  }, [user]);

  const handleLoginSuccess = (data) => {
    setLoginNotice('');
    setUser(data.user);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('username');
      setUser(null);
    }
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Add a small floating toggle button when drawer is closed
  const FloatingMenuButton = () => {
    if (isMobile || drawerOpen) return null;

    return (
      <IconButton
        onClick={toggleDrawer}
        sx={{
          position: 'fixed',
          left: 10,
          bottom: 20,
          bgcolor: 'primary.main',
          color: 'white',
          boxShadow: 3,
          zIndex: 1300,
          '&:hover': {
            bgcolor: 'primary.dark'
          }
        }}
        size="small"
      >
        <MenuIcon />
      </IconButton>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <Router>
          <Routes>
            <Route path="/inspection/:referenceNumber" element={<InspectionPage />} />
            <Route path="/reassignment/:referenceNumber" element={<ReassignmentPage />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={user ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                {/* App Bar */}
                <AppBar
                  position="fixed"
                  color="default"
                  elevation={0}
                  sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px !important', px: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        color="primary"
                        edge="start"
                        onClick={toggleDrawer}
                        sx={{ mr: 2 }}
                        aria-label="menu"
                      >
                        {drawerOpen ? <ChevronLeft /> : <MenuIcon />}
                      </IconButton>
                      <Box component="img" src={logo} alt="iNube" sx={{ height: 32, width: 'auto', mr: 2 }} />
                      <Divider orientation="vertical" flexItem sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="h5" color="text.primary">Motor Pre-Inspection</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>{pt('operationsPortal')}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
                      <LanguageSwitcher compact />
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: 'primary.main',
                            fontSize: '0.9rem',
                            mr: 1.5
                          }}
                        >
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
                            {user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                            {user.role}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={handleLogout}
                          sx={{ ml: 1 }}
                          color="default"
                        >
                          <Logout fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Toolbar>
                </AppBar>
                <Toolbar /> {/* Empty toolbar to offset content */}

                {/* Main Content */}
                <Box sx={{
                  display: 'flex',
                  flexGrow: 1,
                  bgcolor: 'background.default',
                  position: 'relative'
                }}>
                  {/* Sidebar - Now a Drawer component */}
                  <Drawer
                    variant={isMobile ? "temporary" : "persistent"}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        width: 260,
                        bgcolor: 'background.paper',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        pt: 0,
                        height: '100%',
                        overflow: 'hidden'
                      }
                    }}
                    sx={{
                      width: drawerOpen ? 260 : 0,
                      flexShrink: 0,
                      '& .MuiDrawer-paper': {
                        width: 260,
                        boxSizing: 'border-box',
                      },
                    }}
                  >
                    <Toolbar /> {/* Empty toolbar to offset content */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      overflow: 'hidden'
                    }}>
                      {/* User Profile Section */}
                      <Box sx={{
                        px: 2.5,
                        py: 2,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1.5,
                      }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: 'primary.main',
                          }}
                        >
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body1" fontWeight={600} noWrap>{user.username}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{user.role}</Typography>
                        </Box>
                      </Box>

                      <Divider />

                      {/* Navigation Menu */}
                      <Box sx={{
                        p: 1.5,
                        flexGrow: 1,
                        overflow: 'auto',
                      }}>
                        <List sx={{ px: 0 }}>
                          <ListItem
                            button
                            onClick={() => setActivePage('dashboard')}
                            disableGutters
                            sx={{
                              mb: 1,
                              borderRadius: 1,
                              overflow: 'hidden',
                              bgcolor: activePage === 'dashboard' ? 'rgba(21,101,192,0.08)' : 'transparent',
                              color: activePage === 'dashboard' ? 'primary.dark' : 'text.primary',
                              position: 'relative',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: '20%',
                                height: '60%',
                                width: 4,
                                bgcolor: activePage === 'dashboard' ? 'primary.main' : 'transparent',
                                borderTopRightRadius: 4,
                                borderBottomRightRadius: 4,
                              },
                              '&:hover': {
                                bgcolor: 'rgba(21,101,192,0.08)',
                              }
                            }}
                          >
                            <Box sx={{ width: 4 }} />
                            <ListItemIcon sx={{ ml: 1.5, color: 'primary.main' }}>
                              <DashboardIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('app.dashboard')}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                fontSize: '0.95rem'
                              }}
                            />
                          </ListItem>

                          <ListItem
                            button
                            disableGutters
                            onClick={() => setActivePage('create-case')}
                            sx={{
                              mb: 1,
                              borderRadius: 1,
                              pl: 2,
                              bgcolor: activePage === 'create-case' ? 'rgba(21,101,192,0.08)' : 'transparent',
                              '&:hover': { bgcolor: 'rgba(21,101,192,0.04)' }
                            }}
                          >
                            <ListItemIcon sx={{ color: activePage === 'create-case' ? 'primary.main' : 'text.secondary' }}>
                              <Assignment />
                            </ListItemIcon>
                            <ListItemText
                              primary={pt('createCase')}
                              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                            />
                          </ListItem>

                          {['admin', 'supervisor'].includes(String(user.role).toLowerCase()) && (
                            <ListItem
                              button
                              disableGutters
                              onClick={() => {
                                setActivePage('service-logs');
                                if (isMobile) setDrawerOpen(false);
                              }}
                              sx={{
                                mb: 1,
                                borderRadius: 1,
                                pl: 2,
                                bgcolor: activePage === 'service-logs' ? 'rgba(21,101,192,0.08)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(21,101,192,0.04)' }
                              }}
                            >
                              <ListItemIcon sx={{ color: activePage === 'service-logs' ? 'primary.main' : 'text.secondary' }}>
                                <ReceiptLong />
                              </ListItemIcon>
                              <ListItemText
                                primary={pt('serviceLogs')}
                                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                              />
                            </ListItem>
                          )}

                          {String(user.role).toLowerCase() === 'admin' && (
                            <ListItem
                              button
                              disableGutters
                              onClick={() => {
                                setActivePage('user-management');
                                if (isMobile) setDrawerOpen(false);
                              }}
                              sx={{
                                mb: 1,
                                borderRadius: 1,
                                pl: 2,
                                bgcolor: activePage === 'user-management' ? 'rgba(21,101,192,0.08)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(21,101,192,0.04)' }
                              }}
                            >
                              <ListItemIcon sx={{ color: activePage === 'user-management' ? 'primary.main' : 'text.secondary' }}>
                                <ManageAccounts />
                              </ListItemIcon>
                              <ListItemText
                                primary={pt('userManagement')}
                                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                              />
                            </ListItem>
                          )}

                          <ListItem
                            button
                            disableGutters
                            onClick={() => {
                              setActivePage('graphs');
                              if (isMobile) setDrawerOpen(false);
                            }}
                            sx={{
                              mb: 1,
                              borderRadius: 1,
                              pl: 2,
                              bgcolor: activePage === 'graphs' ? 'rgba(21,101,192,0.08)' : 'transparent',
                              '&:hover': { bgcolor: 'rgba(21,101,192,0.04)' }
                            }}
                          >
                            <ListItemIcon sx={{ color: activePage === 'graphs' ? 'primary.main' : 'text.secondary' }}>
                              <QueryStats />
                            </ListItemIcon>
                            <ListItemText
                              primary={pt('graphs')}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                color: 'text.primary'
                              }}
                            />
                          </ListItem>

                          <ListItem
                            button
                            disableGutters
                            onClick={() => {
                              setActivePage('reports');
                              if (isMobile) setDrawerOpen(false);
                            }}
                            sx={{
                              mb: 1,
                              borderRadius: 1,
                              pl: 2,
                              bgcolor: activePage === 'reports' ? 'rgba(21,101,192,0.08)' : 'transparent',
                              '&:hover': { bgcolor: 'rgba(21,101,192,0.04)' }
                            }}
                          >
                            <ListItemIcon sx={{ color: activePage === 'reports' ? 'primary.main' : 'text.secondary' }}>
                              <Assessment />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('app.reports')}
                              primaryTypographyProps={{
                                fontWeight: 500,
                                fontSize: '0.95rem',
                                color: 'text.primary'
                              }}
                            />
                          </ListItem>

                          <ListItem
                            button
                            disableGutters
                            onClick={() => {
                              setActivePage('api-docs');
                              if (isMobile) setDrawerOpen(false);
                            }}
                            sx={{
                              mb: 1,
                              borderRadius: 1,
                              pl: 2,
                              bgcolor: activePage === 'api-docs' ? 'rgba(21,101,192,0.08)' : 'transparent',
                              '&:hover': {
                                bgcolor: 'rgba(21,101,192,0.04)',
                              }
                            }}
                          >
                            <ListItemIcon sx={{ color: activePage === 'api-docs' ? 'primary.main' : 'text.secondary' }}>
                              <Api />
                            </ListItemIcon>
                            <ListItemText
                              primary={pt('apiDocumentation')}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                color: 'text.primary'
                              }}
                            />
                          </ListItem>
                        </List>
                      </Box>

                      {/* Footer */}
                      <Box sx={{ p: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          startIcon={<Logout />}
                          onClick={handleLogout}
                        >
                          {t('app.logout')}
                        </Button>
                      </Box>
                    </Box>
                  </Drawer>

                  {/* Mini drawer for collapsed state - Now hidden */}
                  {!drawerOpen && !isMobile && false && (
                    <Box sx={{
                      width: 0,
                      display: 'none',
                      bgcolor: 'background.paper',
                      flexDirection: 'column',
                      alignItems: 'center',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      pt: 0,
                      height: '100%',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <Toolbar /> {/* Empty toolbar to offset content */}

                      {/* Mini User Avatar */}
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: 'primary.main',
                          my: 2
                        }}
                      >
                        {user.username.charAt(0).toUpperCase()}
                      </Avatar>

                      <Divider sx={{ width: '80%', mb: 2 }} />

                      {/* Mini Navigation */}
                      <Tooltip title="Dashboard" placement="right">
                        <IconButton
                          sx={{
                            mb: 2,
                            bgcolor: 'primary.light',
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.light' },
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: -8,
                              height: 24,
                              width: 3,
                              bgcolor: 'primary.main',
                              borderTopRightRadius: 4,
                              borderBottomRightRadius: 4,
                            },
                          }}
                        >
                          <DashboardIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Graphs" placement="right">
                        <IconButton sx={{ mb: 2, color: 'text.secondary' }}>
                          <QueryStats />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Reports" placement="right">
                        <IconButton sx={{ mb: 2, color: 'text.secondary' }}>
                          <Description />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="API Documentation" placement="right">
                        <IconButton sx={{ mb: 2, color: 'text.secondary' }}>
                          <Api />
                        </IconButton>
                      </Tooltip>

                      <Box sx={{ flexGrow: 1 }} />

                      {/* Toggle button at bottom */}
                      <Tooltip title="Expand Menu" placement="right">
                        <IconButton
                          onClick={toggleDrawer}
                          sx={{ mb: 2, color: 'primary.main' }}
                        >
                          <ChevronRight />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}

                  {/* Content Area */}
                  <Box sx={{
                    flexGrow: 1,
                    p: { xs: 2, md: 3 },
                    transition: 'all 0.2s ease',
                    ml: 0,
                    maxWidth: '100%',
                    overflow: 'auto'
                  }}>
                    {activePage === 'create-case'
                      ? <CreateCasePage onCreated={() => {}} drawerOpen={drawerOpen} isMobile={isMobile} />
                      : activePage === 'user-management' && String(user.role).toLowerCase() === 'admin'
                        ? <UserManagementPage />
                      : activePage === 'service-logs'
                        ? <ServiceLogsPage />
                        : activePage === 'graphs'
                          ? <GraphsPage />
                          : activePage === 'reports'
                            ? <ReportsPage />
                            : activePage === 'api-docs'
                              ? <ApiDocumentationPage />
                              : <Dashboard />}
                  </Box>
                  {/* Add floating menu button */}
                  <FloatingMenuButton />
                </Box>
              </Box>
            ) : (
              <Login onLoginSuccess={handleLoginSuccess} notice={loginNotice} />
            )} />
          </Routes>
        </Router>
      </div>
    </ThemeProvider>
  );
}



export default App;
